import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormControl, FormHelperText, FormLabel, Input } from "@mui/joy";
import { IconButton } from "@mui/material";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Typography } from "@mui/material";
import "../../src/css/AttorneyForm.css";
import { changeUsFormatNumber } from "../components/Common";
import { myAxios } from "../services/helper";
import { Dialog, Button } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { verifyCode } from "../services/user-service";
import { checkEmail } from "../services/user-service";

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{9,}$/;

export default function DirectOrgUser({ onBack }) {
  const [data, setData] = useState({
    username: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phoneNo: "",
    street: "",
    apartment: "",
    city: "",
    zipCode: "",
    state: "",
    country: "",
  });

  const [errors, setErrors] = useState({
    zipValid: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [emailExists, setEmailExists] = useState(false);

  const handleChange = (e, field) => {
    let value = e.target.value;

    if (field === "phoneNo") {
      value = changeUsFormatNumber(value);
    }

    if (field === "zipCode" && !/^\d{0,5}$/.test(value)) return;
    if (field === "password") {
      if (!PASSWORD_REGEX.test(value)) {
        setPasswordError("Password weak");
      } else {
        setPasswordError("");
      }
    }

    setData((prev) => ({ ...prev, [field]: value }));
  };

  /* ZIP → CITY / STATE */
  useEffect(() => {
    if (data.zipCode.length === 5) {
      myAxios
        .get(`https://api.zippopotam.us/us/${data.zipCode}`, {
          baseURL: "", // IMPORTANT: external API
        })
        .then((res) => {
          const place = res.data.places[0];
          setData((prev) => ({
            ...prev,
            city: place["place name"],
            state: place["state abbreviation"],
            country: res.data["country abbreviation"],
          }));
          setErrors((prev) => ({ ...prev, zipValid: true }));
        })
        .catch(() => {
          setErrors((prev) => ({ ...prev, zipValid: false }));
        });
    }
  }, [data.zipCode]);

  const submit = async (e) => {
    e.preventDefault();

    if (
      !data.firstName ||
      !data.email ||
      !data.password ||
      !data.username
    ) {
      toast.error("Please fill all required fields");
      return;
    }
    // 👇 ADD THIS
    try {
      const exists = await checkEmail(data.email);
      if (exists) {
        setEmailExists(true);
        toast.error("Email already exists");
        return;
      } else {
        setEmailExists(false);
      }
    } catch {
      toast.error("Error checking email");
      return;
    }

    if (passwordError) {
      toast.error(
        "Password must be at least 9 characters with uppercase, lowercase, number and special character"
      );
      return;
    }

    try {
      await myAxios.post("/user/tenant/direct-org-user", data, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });

      const qrResponse = await myAxios.post(
        "/user/initiate-qr",
        { username: data.username },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      setQrCodeUrl(qrResponse.data);
      setShowQRDialog(true);

      toast.info("Scan QR and verify OTP to activate user");

    } catch (err) {
      toast.error(err.response?.data || "Error creating organization user");
    }
  };

   const handleVerifyOTP = async () => {
     if (otpCode.length !== 6) {
       toast.error("Enter valid 6-digit OTP");
       return;
     }

     setIsVerifying(true);
     try {
       const payload = {
         username: data.username,
         otp: otpCode,
       };

       const verified = await verifyCode(payload);

       if (verified) {
         toast.success("2FA verified successfully!");
         setShowQRDialog(false);
         onBack(); // ✅ close popup ONLY after 2FA
       } else {
         toast.error("Invalid OTP");
       }
     } catch {
       toast.error("OTP verification failed");
     } finally {
       setIsVerifying(false);
     }
   };

  return (
    <div className="joint_registration_container">
      <div className="joint_registration_form">

        {/* HEADER */}
        <div className="joint_registration_form_heading">
          <h1 style={{ textAlign: "center", width: "100%" }}>
            Organization User Account
          </h1>

          <div className="joint_registration_cancel_icon">
            <FontAwesomeIcon icon={faTimes} onClick={onBack} />
          </div>
        </div>

        {/* FORM */}
        <form className="joint_registration_form_main" onSubmit={submit}>
          <div className="joint_account_form">
            <div className="joint_form_left">

              <FormControl>
                <FormLabel required>First Name</FormLabel>
                <Input
                  placeholder="Enter first name"
                  value={data.firstName}
                  onChange={(e) => handleChange(e, "firstName")}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Last Name</FormLabel>
                <Input
                  placeholder="Enter last name"
                  value={data.lastName}
                  onChange={(e) => handleChange(e, "lastName")}
                />
              </FormControl>

              <FormControl>
                <FormLabel required>Username</FormLabel>
                <Input
                  placeholder="Enter username"
                  value={data.username}
                  onChange={(e) => handleChange(e, "username")}
                />
              </FormControl>

              <FormControl>
                <FormLabel required>Email</FormLabel>
                <Input
                  placeholder="Enter email"
                  value={data.email}
                  onChange={(e) => handleChange(e, "email")}
                />
                {emailExists && (
                  <FormHelperText error>
                    <Typography variant="caption" color="error">
                      Email already exists
                    </Typography>
                  </FormHelperText>
                )}
              </FormControl>

            <FormControl>
              <FormLabel required>Password</FormLabel>

              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={data.password}
                onChange={(e) => handleChange(e, "password")}
                endDecorator={
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </IconButton>
                }
              />

              {passwordError && (
                <FormHelperText error>
                  <Typography variant="caption" color="error">
                    Password weak
                  </Typography>
                </FormHelperText>
              )}
            </FormControl>

              <FormControl>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  placeholder="Enter phone number"
                  value={data.phoneNo}
                  onChange={(e) => handleChange(e, "phoneNo")}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Street</FormLabel>
                <Input
                  placeholder="Enter street"
                  value={data.street}
                  onChange={(e) => handleChange(e, "street")}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Apartment</FormLabel>
                <Input
                  placeholder="Enter apartment"
                  value={data.apartment}
                  onChange={(e) => handleChange(e, "apartment")}
                />
              </FormControl>

              <div className="bene_address">
                <div className="bene_address_half">
                  <FormControl>
                    <FormLabel>Zip Code</FormLabel>
                    <Input
                      placeholder="Enter zip code"
                      value={data.zipCode}
                      onChange={(e) => handleChange(e, "zipCode")}
                    />
                    {!errors.zipValid && (
                      <FormHelperText error>
                        <Typography variant="caption" color="error">
                          Invalid Zip Code
                        </Typography>
                      </FormHelperText>
                    )}
                  </FormControl>
                </div>

                <div className="bene_address_half">
                  <FormControl>
                    <FormLabel>City</FormLabel>
                    <Input value={data.city} disabled />
                  </FormControl>
                </div>
              </div>

              <div className="bene_address">
                <div className="bene_address_half">
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input value={data.state} disabled />
                  </FormControl>
                </div>

                <div className="bene_address_half">
                  <FormControl>
                    <FormLabel>Country</FormLabel>
                    <Input value={data.country} disabled />
                  </FormControl>
                </div>
              </div>

            </div>
          </div>

          {/* BUTTONS */}
          <div className="joint_form_btn_main">
            <div className="joint_form_btn">
              <button type="button" onClick={onBack}>
                Cancel
              </button>
            </div>

            <div className="joint_form_btn">
              <button type="submit">Create</button>
            </div>
          </div>
        </form>

      </div>
      <Dialog open={showQRDialog} maxWidth="xs" fullWidth>
        <div style={{ padding: "24px", textAlign: "center" }}>
          <p style={{ fontSize: "18px", fontWeight: 500 }}>
            Scan this QR using Google Authenticator
          </p>

          {qrCodeUrl && (
            <QRCodeSVG value={qrCodeUrl} size={220} level="H" />
          )}

          <Input
            placeholder="Enter 6-digit OTP"
            value={otpCode}
            onChange={(e) => {
              if (/^\d{0,6}$/.test(e.target.value)) {
                setOtpCode(e.target.value);
              }
            }}
            style={{ marginTop: "16px", textAlign: "center" }}
          />

          <Button
            variant="contained"
            fullWidth
            style={{ marginTop: "16px" }}
            disabled={otpCode.length !== 6 || isVerifying}
            onClick={handleVerifyOTP}
          >
            {isVerifying ? "Verifying..." : "Verify & Activate"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
